import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useGetTicker } from './useTradeStation';
import tickerDetailsCache from '../utils/tickerDetailsCache';
import { getTickerMetadata } from '../utils/tickerSearch';

/**
 * Hook to manage ticker details for watchlist tickers
 * Uses local ticker index first (6,700+ tickers), then falls back to API calls
 * This avoids unnecessary API calls for most tickers
 */
export const useWatchlistTickerDetails = (tickers) => {
  const { user } = useContext(AuthContext);
  const hasTsCredentials = !!user?.hasTradeStationCredentials;
  const [tickerDetailsMap, setTickerDetailsMap] = useState({});
  const [loadingTickers, setLoadingTickers] = useState(() => new Set());
  const tickerDetailsMapRef = useRef(tickerDetailsMap);
  const loadingTickersRef = useRef(loadingTickers);
  const { getTicker } = useGetTicker();

  useEffect(() => {
    tickerDetailsMapRef.current = tickerDetailsMap;
  }, [tickerDetailsMap]);

  useEffect(() => {
    loadingTickersRef.current = loadingTickers;
  }, [loadingTickers]);

  const tickersKey = useMemo(
    () => (tickers || []).map((t) => t.ticker).filter(Boolean).join(','),
    [tickers]
  );

  const fetchTickerDetails = useCallback(
    async (ticker) => {
      if (tickerDetailsMapRef.current[ticker] || loadingTickersRef.current.has(ticker)) {
        return;
      }

      const localMetadata = getTickerMetadata(ticker);
      if (localMetadata) {
        setTickerDetailsMap((prev) => ({
          ...prev,
          [ticker]: {
            Symbol: localMetadata.symbol,
            Description: localMetadata.name,
            Root: localMetadata.symbol,
            AssetType: localMetadata.type === 'futures' ? 'FUTURE' : 'STOCK',
            Category: localMetadata.category,
            Exchange: localMetadata.exchange,
            _fromLocalIndex: true,
          },
        }));
        return;
      }

      if (tickerDetailsCache.has(ticker)) {
        const cachedDetails = tickerDetailsCache.get(ticker);
        if (cachedDetails) {
          setTickerDetailsMap((prev) => ({
            ...prev,
            [ticker]: cachedDetails,
          }));
          return;
        }
      }

      if (!hasTsCredentials) return;

      setLoadingTickers((prev) => new Set(prev).add(ticker));

      try {
        const details = await getTicker(ticker);
        if (details && !details.Errors?.length && details.Symbols?.[0]) {
          const tickerDetails = details.Symbols[0];
          setTickerDetailsMap((prev) => ({
            ...prev,
            [ticker]: tickerDetails,
          }));
          tickerDetailsCache.set(ticker, tickerDetails);
        }
      } catch (error) {
        console.warn(`Failed to fetch details for ${ticker}:`, error);
      } finally {
        setLoadingTickers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(ticker);
          return newSet;
        });
      }
    },
    [getTicker, hasTsCredentials]
  );

  useEffect(() => {
    if (!tickersKey) return;

    const symbols = tickersKey.split(',').filter(Boolean);
    symbols.forEach((ticker) => {
      fetchTickerDetails(ticker);
    });
  }, [tickersKey, fetchTickerDetails]);

  useEffect(() => {
    if (!tickersKey) {
      setTickerDetailsMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const currentTickers = new Set(tickersKey.split(',').filter(Boolean));
    setTickerDetailsMap((prev) => {
      const keys = Object.keys(prev);
      const allCurrent = keys.every((k) => currentTickers.has(k));
      const complete =
        allCurrent && keys.length === currentTickers.size;
      if (complete) return prev;

      const filtered = {};
      keys.forEach((ticker) => {
        if (currentTickers.has(ticker)) {
          filtered[ticker] = prev[ticker];
        }
      });
      return filtered;
    });
  }, [tickersKey]);

  const getTickerDetails = useCallback(
    (ticker) => tickerDetailsMap[ticker] || null,
    [tickerDetailsMap]
  );

  const isLoading = useCallback(
    (ticker) => loadingTickers.has(ticker),
    [loadingTickers]
  );

  return {
    getTickerDetails,
    isLoading,
    tickerDetailsMap,
  };
};
